import {Paper, Typography, Stack, Box} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import ShoppingIcon from '$assets/svgs/shopping-icon.svg?react';

import {
  chartContainerStyle,
  titleStyle,
  legendContainerStyle,
  legendDotStyle,
  iconWrapperStyle,
  fieldStyle,
  percentageStyle,
  chartRowStyle,
  paperStyle,
  iconTextRowStyle,
} from './styles';
import type {InsuranceClaimStatus} from '$api/finance/dashboard';
import {
  tooltipContainerStyle,
  tooltipTopRowStyle,
  tooltipCountStyle,
  tooltipColorBoxStyle,
  tooltipTextStyle,
} from '../PerferredPaymentMethods/styles';
import {cardStyle} from '../../styles';
import {DashboardData} from '$api/finance/dashboard';
import {Color} from '~/constants/style';
import {INSURANCE_CLAIMS_CHART_CONFIG} from '../../../constant';

interface InsuranceClaimsManagementCardProps {
  data: DashboardData;
}

type ClaimsData = {
  name: string;
  value: number;
  count: number;
  color: string;
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ClaimsData;
  }>;
}

const InsuranceClaimsManagementCard: React.FC<
  InsuranceClaimsManagementCardProps
> = ({data}) => {
  const {
    open = {} as Partial<InsuranceClaimStatus>,
    closed = {} as Partial<InsuranceClaimStatus>,
    rejected = {} as Partial<InsuranceClaimStatus>,
  } = data.insurance_claims_management || {};

  const claimsData = [
    {
      name: 'Open',
      value: open.percentage ?? 0,
      count: open.count ?? 0,
      color: Color.Primary2Darkest,
    },
    {
      name: 'Closed',
      value: closed.percentage ?? 0,
      count: closed.count ?? 0,
      color: Color.Primary2Dark,
    },
    {
      name: 'Rejected',
      value: rejected.percentage ?? 0,
      count: rejected.count ?? 0,
      color: Color.Primary2Main,
    },
  ];

  const CustomTooltip: React.FC<CustomTooltipProps> = ({active, payload}) => {
    if (active && payload && payload.length) {
      const {name, count, color} = payload[0].payload;
      return (
        <Box sx={tooltipContainerStyle}>
          <Box sx={tooltipTopRowStyle}>
            <Typography sx={tooltipCountStyle}>{count}</Typography>
            <Box sx={{...tooltipColorBoxStyle, bgcolor: color}} />
          </Box>
          <Typography sx={tooltipTextStyle}>{`${name} claims`}</Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Paper sx={cardStyle}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={iconTextRowStyle}
      >
        <Box sx={iconWrapperStyle}>
          <ShoppingIcon />
        </Box>

        <Typography variant="h6" component="span" sx={titleStyle}>
          Insurance claims management
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={2} sx={chartRowStyle}>
        <Box sx={chartContainerStyle}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={claimsData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                {...INSURANCE_CLAIMS_CHART_CONFIG}
              >
                {claimsData.map((entry, index) => (
                  <Cell key={`cell-claims-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                content={<CustomTooltip />}
                wrapperStyle={{outline: 'none', zIndex: 1000}}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box>
          {claimsData.map(item => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={legendContainerStyle}
              key={item.name}
            >
              <Box
                sx={{
                  ...legendDotStyle,
                  bgcolor: item.color,
                }}
              />
              <Box sx={paperStyle}>
                <Typography sx={fieldStyle}>{item.name}</Typography>
                <Typography sx={percentageStyle}>{item.value}%</Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

export default InsuranceClaimsManagementCard;
